import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Avatar,
  Divider,
  useTheme,
} from '@mui/material';
import { Warning, Person, SwapHoriz } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const TeamTransferConfirmModal = ({
  open,
  onClose,
  onConfirm,
  user,
  currentTeam,
  newTeam,
  loading = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  if (!user || !currentTeam || !newTeam) {
    return null;
  }

  const userName = `${user.first_name || ''} ${user.last_name || ''}`.trim();

  return (
    <Dialog 
      open={open} 
      onClose={(e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose(e, reason);
      }}
      disableEscapeKeyDown
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: { 
          borderRadius: 3,
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 12px 48px rgba(0,0,0,0.4)' 
            : '0 12px 48px rgba(0,0,0,0.15)',
          bgcolor: 'background.paper'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
            <Warning />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600} color="text.primary">
              {t('teams.transfer.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('teams.transfer.message')}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box 
          sx={{ 
            p: 3, 
            bgcolor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50',
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'
          }}
        >
          {/* User Info */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Person />
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                {userName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.role} - {user.title?.title_name || 'Unvan Yok'}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Team Transfer Info */}
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box textAlign="center" flex={1}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                {t('teams.transfer.currentTeam')}
              </Typography>
              <Typography variant="body1" fontWeight={500} color="error.main">
                {currentTeam.name}
              </Typography>
            </Box>
            
            <SwapHoriz sx={{ mx: 2, color: 'primary.main', fontSize: 32 }} />
            
            <Box textAlign="center" flex={1}>
              <Typography variant="caption" color="text.secondary" textTransform="uppercase">
                {t('teams.transfer.newTeam')}
              </Typography>
              <Typography variant="body1" fontWeight={500} color="success.main">
                {newTeam.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box 
          sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: theme.palette.mode === 'dark' ? 'warning.dark' : 'warning.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'warning.main' : 'warning.200'
          }}
        >
          <Typography 
            variant="body2" 
            color={theme.palette.mode === 'dark' ? 'warning.light' : 'warning.dark'} 
            fontWeight={500}
          >
            ⚠️ {t('teams.transfer.warning')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        gap: 2,
        borderTop: '1px solid',
        borderTopColor: 'divider'
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{ 
            minWidth: 100,
            borderColor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
            color: 'text.secondary',
            '&:hover': {
              borderColor: theme.palette.mode === 'dark' ? 'grey.500' : 'grey.400',
              bgcolor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.50'
            }
          }}
        >
          {t('teams.transfer.cancel')}
        </Button>
        <Button 
          onClick={onConfirm} 
          variant="contained" 
          color="warning"
          disabled={loading}
          sx={{ minWidth: 120 }}
        >
          {loading ? t('common.loading') : t('teams.transfer.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TeamTransferConfirmModal; 